// IndexedDB database setup using idb-keyval
// Centralized database management with versioning and schema

import { createStore, set, get, del, keys, clear } from 'idb-keyval';

// Database stores (namespaces)
const STORES = {
  CACHE: 'gym-cache',
  IMAGES: 'gym-images',
  METADATA: 'gym-metadata',
  SYNC_QUEUE: 'gym-sync-queue',
  SETTINGS: 'gym-settings',
  IMAGE_UPLOADS: 'gym-image-uploads',
};

// Create stores
const cacheStore = createStore(STORES.CACHE, STORES.CACHE);
const imageStore = createStore(STORES.IMAGES, STORES.IMAGES);
const metadataStore = createStore(STORES.METADATA, STORES.METADATA);
const syncQueueStore = createStore(STORES.SYNC_QUEUE, STORES.SYNC_QUEUE);
const settingsStore = createStore(STORES.SETTINGS, STORES.SETTINGS);
const imageUploadsStore = createStore(STORES.IMAGE_UPLOADS, STORES.IMAGE_UPLOADS);

// Database version for migrations
const DB_VERSION = '1.0.0';

/**
 * Database class for centralized IndexedDB operations
 */
class OfflineDatabase {
  constructor() {
    this.version = DB_VERSION;
    this.stores = STORES;
    this._ready = null;
  }

  /**
   * Returns a promise that resolves when the DB is fully initialized.
   * Subsequent calls return the same promise so multiple callers can await it.
   * Public so anything that needs the DB can await db.ready() first.
   */
  ready() {
    if (!this._ready) {
      this._ready = this._initialize();
    }
    return this._ready;
  }

  /**
   * Initialize database and run migrations if needed.
   * Uses raw idb-keyval to avoid circular ready() → _initialize() → get() → ready().
   */
  async _initialize() {
    try {
      const currentVersion = await get('db_version', metadataStore);
      
      if (!currentVersion) {
        await set('db_version', this.version, metadataStore);
        console.log('[OfflineDB] Database initialized', this.version);
      } else if (currentVersion !== this.version) {
        await this._migrate(currentVersion, this.version);
      }
      
      return true;
    } catch (error) {
      console.error('[OfflineDB] Initialization failed:', error);
      return false;
    }
  }

  /** @deprecated Use ready() instead */
  async initialize() {
    return this.ready();
  }

  /**
   * Migration handler for future database schema changes
   */
  async _migrate(fromVersion, toVersion) {
    console.log(`[OfflineDB] Migrating from ${fromVersion} to ${toVersion}`);
    await set('db_version', toVersion, metadataStore);
  }

  /**
   * Generic get operation
   */
  async get(key, store = cacheStore) {
    try {
      await this.ready();
      return await get(key, store);
    } catch (error) {
      console.error('[OfflineDB] Get failed:', error);
      return null;
    }
  }

  /**
   * Generic set operation
   */
  async set(key, value, store = cacheStore) {
    try {
      await this.ready();
      await set(key, value, store);
      return true;
    } catch (error) {
      console.error('[OfflineDB] Set failed:', error);
      return false;
    }
  }

  /**
   * Generic delete operation
   */
  async delete(key, store = cacheStore) {
    try {
      await this.ready();
      await del(key, store);
      return true;
    } catch (error) {
      console.error('[OfflineDB] Delete failed:', error);
      return false;
    }
  }

  /**
   * Get all keys in a store
   */
  async getKeys(store = cacheStore) {
    try {
      await this.ready();
      return await keys(store);
    } catch (error) {
      console.error('[OfflineDB] GetKeys failed:', error);
      return [];
    }
  }

  /**
   * Clear entire store
   */
  async clearStore(store = cacheStore) {
    try {
      await this.ready();
      await clear(store);
      return true;
    } catch (error) {
      console.error('[OfflineDB] ClearStore failed:', error);
      return false;
    }
  }

  /**
   * Get database statistics
   */
  async getStats() {
    try {
      const cacheKeys = await this.getKeys(cacheStore);
      const imageKeys = await this.getKeys(imageStore);
      const metadataKeys = await this.getKeys(metadataStore);
      const queueKeys = await this.getKeys(syncQueueStore);
      const uploadKeys = await this.getKeys(imageUploadsStore);
      
      return {
        version: this.version,
        cacheEntries: cacheKeys.length,
        imageEntries: imageKeys.length,
        metadataEntries: metadataKeys.length,
        queuedMutations: queueKeys.length,
        pendingImageUploads: uploadKeys.length,
      };
    } catch (error) {
      console.error('[OfflineDB] GetStats failed:', error);
      return null;
    }
  }

  /**
   * Clear all offline data (useful for logout or reset)
   */
  async clearAll() {
    try {
      await Promise.all([
        this.clearStore(cacheStore),
        this.clearStore(imageStore),
        this.clearStore(metadataStore),
        this.clearStore(syncQueueStore),
        this.clearStore(imageUploadsStore),
      ]);
      console.log('[OfflineDB] All data cleared');
      return true;
    } catch (error) {
      console.error('[OfflineDB] ClearAll failed:', error);
      return false;
    }
  }
}

// Export singleton instance
export const db = new OfflineDatabase();

// Export stores for repository use
export { cacheStore, imageStore, metadataStore, syncQueueStore, settingsStore, imageUploadsStore };
